import { useNavigate } from "react-router-dom";
import {
  getGetUserArticleGeneratorsQueryKey,
  useDeleteArticleGenerator,
  useGetUserArticleGenerators,
} from "@packages/api/madlenAPI";
import Card from "@features/tools/components/card";
import HeroTitle from "@features/tools/components/tool_components/HeroTitle";
import { Package } from "lucide-react";
import { Button, Text } from "@core/index";
import { useTranslation } from "react-i18next";
import ErrorComponent from "@features/tools/components/tool_components/ErrorComponent";
import { useState } from "react";
import DeleteConfirmPopUp from "@features/tools/components/tool_components/DeleteConfirmPopUp";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@packages/contexts/ToastContext";
function ArticleGeneratorView() {
  const navigate = useNavigate();
  const {
    data: articleGenerators,
    isLoading,
    isError,
    refetch,
  } = useGetUserArticleGenerators();
  const { t } = useTranslation();
  const [showDeletePopup, setShowDeletePopup] = useState<number | undefined>(
    undefined,
  );

  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const deleteArticleGenerator = useDeleteArticleGenerator({
    mutation: {
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: getGetUserArticleGeneratorsQueryKey(),
        });
      },
    },
  });

  const handleDelete = async (articleGeneratorId: number) => {
    try {
      await deleteArticleGenerator.mutateAsync({ id: articleGeneratorId });
      addToast({
        title: t("articleGenerator.deleteSuccess"),
        variant: "success",
        duration: 3000,
      });
      setShowDeletePopup(undefined);
    } catch (error) {
      console.error("Failed to delete article generator:", error);
      addToast({
        title: t("articleGenerator.deleteError"),
        description: t("articleGenerator.deleteErrorDescription"),
        variant: "error",
      });
    }
  };

  const colors = {
    light: "from-blue-500 to-emerald-500",
    dark: "dark:from-blue-400 dark:to-emerald-400",
    iconBg: "bg-blue-100 dark:bg-blue-900/50",
    iconColor: "text-blue-600 dark:text-blue-400",
    hover: "hover:bg-blue-50 dark:hover:bg-blue-900/20",
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-blue-100 dark:from-gray-900 dark:to-blue-900">
      <div className="container max-w-7xl mx-auto px-4 py-4">
        {/* Hero Section */}
        <HeroTitle
          title={t("articleGenerator.title")}
          description="articleGenerator.description"
          onClick={() => navigate("/content/article-generator/create")}
          buttonLink="/content/article-generator/create"
          buttonStyle="bg-gradient-to-r from-blue-600 to-emerald-600  hover:from-blue-700 hover:to-emerald-700"
          titleStyle="bg-gradient-to-r from-blue-600 to-emerald-600"
          titleBackgroundStyle="bg-gradient-to-r from-blue-50/30 to-emerald-50/30 dark:from-blue-900/30 dark:to-emerald-900/30"
        />
        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
            <div
              className={`w-16 h-16 border-4 ${colors.iconColor} border-t-transparent rounded-full animate-spin`}
            ></div>
            <Text
              variant={"span"}
              className="!text-gray-600 dark:!text-gray-300"
              weight={"medium"}
              size={"lg"}
            >
              {`articleGenerator.loading`}
            </Text>
          </div>
        )}

        {/* Error State */}
        {isError && (
          <ErrorComponent
            title={t(`articleGenerator.error.title`)}
            refetch={refetch}
            description={t(`articleGenerator.error.description`)}
            retryButton={t(`articleGenerator.error.retryButton`)}
          />
        )}
        {!isLoading && !isError && articleGenerators?.data && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articleGenerators.data.map((article) => (
              <Card
                key={article.id}
                item={{
                  id: String(article.id),
                  title: article.title || article.prompt || "Untitled Article",
                  imageUrl: article.imageUrl || "",
                  videoUrl: article.videoUrl,
                  prompt: article.prompt || article.title || "Untitled Article",
                  description: article.description || "",
                  courseName: article.courseName || "UNKNOWN",
                  gradeLevel: article.gradeLevel || 0,
                  createdAt: article.createdAt,
                }}
                selectItem={() => {
                  navigate(`/content/article-generator/article/${article.id}`);
                }}
                setShowDeletePopup={() => setShowDeletePopup(article.id)}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading &&
          !isError &&
          (!articleGenerators?.data || articleGenerators.data.length === 0) && (
            <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4 bg-gray-50 dark:bg-gray-900/20 rounded-2xl p-8">
              <div className={`p-4 rounded-full ${colors.iconBg}`}>
                <Package size={32} className={colors.iconColor} />
              </div>
              <Text variant={"h2"}>{`articleGenerator.empty.title`}</Text>
              <Text variant="p" status="subtle" size={"base"}>
                {`articleGenerator.empty.description`}
              </Text>
              <Button
                onClick={() => navigate(`/content/article-generator/create`)}
                variant="solid"
                size="md"
                rounded="lg"
                textColor="text-white"
                bgColor={`hover:opacity-90 ${colors.light ? `${colors.light} bg-gradient-to-r` : "bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700"}`}
              >
                {t(`articleGenerator.empty.createButton`)}
              </Button>
            </div>
          )}
      </div>
      {showDeletePopup && (
        <DeleteConfirmPopUp
          title={t("articleGenerator.deleteConfirmation.title", {
            title: "Article",
          })}
          showDeletePopup={showDeletePopup}
          setShowDeletePopup={setShowDeletePopup}
          handleDelete={handleDelete}
        />
      )}
    </div>
  );
}

export default ArticleGeneratorView;
